/**
 * Approvals analytics utilities
 *
 * Shared constants and helpers for analytics events fired across the Approvals workflow.
 * All approval-related events should use the APPROVALS_ANALYTICS_SOURCE constant as their source.
 */
import type { Approver } from './ApprovalsContext.tsx';

/**
 * Analytics source identifier for all Approvals workflow events.
 * Used as the `source` attribute in UI, Track, and Screen events.
 */
export const APPROVALS_ANALYTICS_SOURCE = 'ConfluenceApprovalsWorkflow';

/**
 * Computes per-status counts from an array of approvers.
 *
 * Returns an object matching the `updatedReviewStatus` / `currentReviewStatus`
 * shape required by the Approvals Analytics Spec.
 */
export const computeReviewStatusCounts = (
	approvers: Approver[],
): { approved: number; declined: number; pending: number; total: number } =>
	approvers.reduce(
		(counts, approver) => {
			if (approver.status === 'REVIEWER_APPROVED') {
				counts.approved++;
			} else if (approver.status === 'REVIEWER_DENIED') {
				counts.declined++;
			} else if (approver.status === 'PENDING_REVIEW') {
				counts.pending++;
			}
			return counts;
		},
		{ approved: 0, declined: 0, pending: 0, total: approvers.length },
	);

/**
 * Shape of a mutation error extension from the GraphQL response.
 *
 * The AGG schema returns `extensions` as a single object on each error
 * (not an array). Some older call sites may still pass an array shape,
 * so both are accepted by `getFailureStatusCode`.
 */
type MutationErrorExtension = {
	errorType?: string | null;
	statusCode?: number | null;
};

type MutationError = {
	extensions?: MutationErrorExtension | MutationErrorExtension[] | null;
	message?: string | null;
};

/**
 * Extracts the first `statusCode` from a mutation error's extensions.
 *
 * Accepts the AGG schema shape where `extensions` is a single object
 * (`{ errorType, statusCode }`), as well as the legacy array shape
 * (`[{ errorType, statusCode }]`).
 *
 * Returns null when the error does not match the expected shape or contains
 * no statusCode.
 */
export const getFailureStatusCode = (error: unknown): number | null => {
	if (error == null || typeof error !== 'object') {
		return null;
	}

	const mutationError = error as MutationError;
	const { extensions } = mutationError;

	if (extensions == null) {
		return null;
	}

	const ext = Array.isArray(extensions) ? extensions[0] : extensions;
	if (ext == null) {
		return null;
	}

	const { statusCode } = ext;
	if (typeof statusCode === 'number') {
		return statusCode;
	}

	return null;
};
